// 치과 「진료 후 질문」(질문 로드맵 엔진 ②·⑤) — Cloud Functions 호출 모음 (2026-10-06, 플래그 roadmapEngine OFF 출고)
// 서버 계약: docs/계약_질문로드맵_웹_2026-10-06.md. 계약이 바뀌면 이 파일 한 곳만 고친다.
// 화면 컴포넌트는 CF 이름·입출력 형태를 직접 알지 않는다. 문구는 응답 labels·catalog 로 받아 그대로 표시(법무 RM §2-3 — 점수·등급·경보색·결론 라벨을 웹에서 만들지 않는다).
// 공통: region asia-northeast3, onCall. 플래그 OFF면 failed-precondition {code:'feature-off'} (베타 미리보기 없음).
// ⚠️ 콘솔에는 오류 코드만 남긴다 — 환자명·답 내용·전화번호는 절대 로그에 쓰지 않는다.
import { getFunctions, httpsCallable } from 'firebase/functions';
import { isAppCheckError, APP_CHECK_REQUIRED_TEXT } from '../utils/appCheckError';

const REGION = 'asia-northeast3';
const call = (name) => httpsCallable(getFunctions(undefined, REGION), name);

export const ROADMAP_GENERIC_ERROR = '요청을 처리하지 못했습니다. 잠시 후 다시 시도해 주세요.';

// 2차 활성 트랙(보철 4개, 기준일 = [장착 완료]) — 버튼 라벨·잠금 여부는 getRoadmapSettings 응답 catalog 가 정본. 이 목록은 응답을 못 받았을 때의 폴백 판정에만 쓴다
export const PROSTHESIS_TRACK_KEYS = Object.freeze(['inlay', 'crown_bridge', 'implant', 'denture']);
export const BASIS_LOOKBACK_DAYS = 30;   // 서버 limits.basisLookbackDays 기본값(응답 limits 가 있으면 그 값 우선)

// T-18 직원 초대 화면 고지 — 서버 상수 CLINIC_INBOX_LABELS.staffInviteNotice 와 같은 문자열(계약 §4). 초대 화면에는 로드맵 응답이 없어 여기 둔다
export const STAFF_INVITE_NOTICE = '질문 답은 직원 개인별 계정으로만 볼 수 있습니다. 여러 사람이 함께 쓰는 계정을 만들지 마세요. 퇴사한 직원의 계정은 바로 해제해 주세요.';
// T-17 [진료 끝 ▾] 비활성 사유 — 서버 labels.consentOff 와 같은 문자열(응답이 있으면 응답값 우선)
export const CONSENT_OFF_TEXT = '환자가 앱에서 질문 받기를 켜지 않았습니다.';
// T-19 켜기 차단 — 서버 labels.enableBlocked 와 같은 문자열(응답이 있으면 응답값 우선)
export const ENABLE_BLOCKED_TEXT = '진료 후 질문을 켜려면 검증된 대표 전화번호 등록과 환자 앱 등록 처리위탁 확인이 필요합니다.';

// 레코드에 질문 동의(aftercare)가 켜져 있는가 — 계약 §2: consents.aftercare.agreed !== true 면 버튼 비활성(서버도 같은 조건으로 거부·저장 0)
export const hasAftercareConsent = (rec) => !!(rec && rec.consents && rec.consents.aftercare && rec.consents.aftercare.agreed === true && !rec.consents.aftercare.revokedAt);

export const WEEKDAY_LABELS = Object.freeze(['일', '월', '화', '수', '목', '금', '토']);

// 오늘 'YYYY-MM-DD'(브라우저 로컬 — 치과 PC 기준). 서버는 KST 로 검증한다
export const todayKey = (d = new Date()) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
export const addDaysKey = (key, n) => { const d = new Date(`${key}T00:00:00`); d.setDate(d.getDate() + n); return todayKey(d); };
export const fmtDayKey = (key) => (key ? String(key).replace(/-/g, '.') : '');
export const fmtMs = (ms, withTime = false) => {
    if (!ms) return '';
    const d = new Date(typeof ms === 'number' ? ms : (ms._seconds ? ms._seconds * 1000 : ms));
    if (Number.isNaN(d.getTime())) return '';
    const base = `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
    return withTime ? `${base} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}` : base;
};
// E.164(+8210…) → 010-1234-5678 표시(화면 표시만 — 저장·복사 경로 없음)
export const fmtPhone = (e164) => {
    const d = String(e164 || '').replace(/[^0-9+]/g, '');
    const m = d.match(/^\+82(1[0-9])(\d{3,4})(\d{4})$/);
    if (m) return `0${m[1]}-${m[2]}-${m[3]}`;
    const k = d.match(/^0(1[0-9])(\d{3,4})(\d{4})$/);
    if (k) return `0${k[1]}-${k[2]}-${k[3]}`;
    return d;
};

// 오류 코드 → 화면 문구 (계약 §1·§2·§3-3). 서버 message 가 있는 코드는 그 문구를 우선 쓴다(문구 정본 = 서버)
const CODE_TEXT = Object.freeze({
    'feature-off': '진료 후 질문 기능이 아직 열리지 않았습니다.',
    'consent-required': CONSENT_OFF_TEXT,                                           // T-17
    'no-verified-device': '환자 휴대폰에서 앱 확인이 끝나지 않았습니다',                // 계약 §2
    'track-active': '같은 진료 유형의 질문이 진행 중입니다 — 종료 후 다시',             // 계약 §2
    'track-locked': '아직 열리지 않은 진료 유형입니다',
    'track-off': '이 진료 유형은 설정에서 꺼져 있습니다',
    'roadmap-mode-off': '설정에서 진료 후 질문(로드맵)을 켠 뒤 사용할 수 있습니다',
    'clinic-phone-required': ENABLE_BLOCKED_TEXT,                                   // T-19
    'no-phone': ENABLE_BLOCKED_TEXT,                                                // T-19 (별칭)
    'delegation-reconfirm-required': ENABLE_BLOCKED_TEXT,                           // T-19
    'delegation-required': ENABLE_BLOCKED_TEXT,                                     // T-19 (별칭)
    'record-inactive': '등록 상태가 바뀌어 시작할 수 없습니다',
    'track-no-steps': '기준일이 오래되어 보낼 단계가 없습니다',
    'access-log-unavailable': '잠시 후 다시 시도해 주세요',
    'answer-content-deleted': '이미 내용이 삭제된 답입니다',
});

export function describeRoadmapError(e, fallback = ROADMAP_GENERIC_ERROR) {
    console.error('[careRoadmapApi]', e?.code || '', e?.details?.code || '');
    if (isAppCheckError(e)) return APP_CHECK_REQUIRED_TEXT;
    const c = e?.details?.code;
    if (e?.code === 'functions/unauthenticated') return '로그인이 필요합니다.';
    if (e?.code === 'functions/permission-denied') return e?.message || '권한이 없습니다.';
    if (e?.code === 'functions/resource-exhausted') return '요청이 너무 많습니다. 잠시 후 다시 시도해 주세요.';
    if (e?.code === 'functions/not-found') return e?.message || '찾을 수 없습니다. 화면을 새로고침해 주세요.';
    if (typeof c === 'string') {
        if (c.startsWith('status:')) return '이미 처리되었거나 확인할 수 없는 상태입니다. 화면을 새로고침해 주세요.';
        if (c.startsWith('basis-day-')) return e?.message || '기준일이 올바르지 않습니다.';
        if (c.startsWith('offsets-') || c.startsWith('closed-days-') || c === 'track-unknown') return e?.message || '입력 값이 올바르지 않습니다.';
        if (CODE_TEXT[c]) return CODE_TEXT[c];
    }
    if (e?.code === 'functions/failed-precondition' || e?.code === 'functions/unavailable') return e?.message || fallback;
    if (e?.code === 'functions/invalid-argument') return e?.message || '요청 값이 올바르지 않습니다. 화면을 새로고침해 주세요.';
    return fallback;
}
export const roadmapErrorCode = (e) => (typeof e?.details?.code === 'string' ? e.details.code : '');

// ── 계약 §1 설정 ──
export async function getRoadmapSettings() {
    const res = await call('getRoadmapSettings')({});
    return res.data || { ok: false };
}
export async function setRoadmapSettings(payload) {
    const res = await call('setRoadmapSettings')(payload);
    return res.data || { ok: false };
}

// ── 계약 §2 [진료 끝 ▾]/[장착 완료] ──
export async function startCareTrack({ pid, track, basisDay, orderId }) {
    const payload = { pid, track };
    if (basisDay) payload.basisDay = basisDay;
    if (orderId) payload.orderId = orderId;
    const res = await call('startCareTrack')(payload);
    return res.data || { ok: false };
}
// 계약 §6 — 환자 상세 트랙 목록(진료 유형 label 은 상세에서만 표시). 접속기록 track_list
// → { ok, pid, items:[{trackId, track, label, status, basisDay, basisSource, stepIndex, stepsTotal, sendCount, nextDayKey, endReason, createdAt, endedAt, createdByRole}], active:[track], consent, consentReason, consentOffLabel, canCancel, tracksAvailable }
export async function listPatientCareTracks(pid) {
    const res = await call('listPatientCareTracks')({ pid });
    return res.data || { ok: false, items: [] };
}
export async function cancelCareTrack({ pid, trackId }) {
    const res = await call('cancelCareTrack')({ pid, trackId });
    return res.data || { ok: false };
}

// ── 계약 §3 수신함 ──
export const INBOX_BUCKETS = Object.freeze(['first_look', 'to_check', 'done']);
export const INBOX_PAGE = 100;
export async function listClinicRoadmapAnswers({ bucket, limit, cursor, withGate } = {}) {
    const payload = {};
    if (withGate === true) payload.withGate = true;   // 10-15: 파일럿 판정까지 서버 1회(useRoadmapInbox opts.gate)
    if (bucket) payload.bucket = bucket;
    if (Number.isInteger(limit)) payload.limit = limit;
    if (Number.isFinite(cursor)) payload.cursor = cursor;
    const res = await call('listClinicRoadmapAnswers')(payload);
    return res.data || { ok: false, sections: {}, summary: {}, labels: {} };
}
export async function getRoadmapInboxCount() {
    const res = await call('getRoadmapInboxCount')({});
    return res.data || { ok: false, toCheckCount: 0 };
}
export async function acknowledgeRoadmapAnswer(fbId) {
    const res = await call('acknowledgeRoadmapAnswer')({ fbId });
    return res.data || { ok: false };
}
export async function stopRoadmapPhrase(fbId) {
    const res = await call('stopRoadmapPhrase')({ fbId });
    return res.data || { ok: false };
}
export async function revealRoadmapPhone(pid) {
    const res = await call('revealRoadmapPhone')({ pid });
    return res.data || { ok: false };
}
